import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion } from 'framer-motion';
import { XCircle, SearchIcon, X, CheckSquare } from 'lucide-react';
import { message } from 'antd';
import Select from 'react-select';

const CompletionModal = ({ requirement, onClose, year, clients }) => {
  const [loading, setLoading] = useState(false);
  const [localStatuses, setLocalStatuses] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  
  // Add new state variables for API data
  const [checkedData, setCheckedData] = useState({ clients_checked: [] });
  const [notRequiredData, setNotRequiredData] = useState({ not_required: [] });
  const [pendingData, setPendingData] = useState({ pending_clients: [] });

  // Helper to PUT status changes to your server
  const saveStatusesToServer = async (updatedStatuses, previousStatuses) => {
    try {
      setLoading(true);
      const userId = localStorage.getItem('userid');

      // Log before/after states for debugging
      console.log('[CompletionModal] Previous statuses:', previousStatuses);
      console.log('[CompletionModal] Updated statuses:', updatedStatuses);

      // Figure out which changes need to be sent to the API
      const changes = Object.entries(updatedStatuses).reduce((acc, [clientId, newStatus]) => {
        if (previousStatuses[clientId] !== newStatus) {
          // Find the client name using the clientId
          const client = (clients || []).find(c => c.unid === clientId);
          if (client) {
            acc.push({
              clientName: client['Company Name'],
              status: newStatus
            });
          }
        }
        return acc;
      }, []);

      console.log('[CompletionModal] Changes to send:', changes);

      // PUT each status update individually
      const updatePromises = changes.map(({ clientName, status }) => {
        const url = `https://plansync-backend-234288438538.us-central1.run.app/api/requirements-checklist/${year}/${userId}/update-status/${requirement.unid}`;
        const body = {
          company: clientName,
          status: status === 'not required' ? 'not_required' : status.toLowerCase()
        };

        console.log('[CompletionModal] Sending PUT:', { url, body });

        return fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        });
      });

      // Wait for all updates to finish
      await Promise.all(updatePromises);

      message.success('Status updated successfully');
      console.log('[CompletionModal] Status update complete successfully');
    } catch (error) {
      console.error('[CompletionModal] Error updating completion statuses:', error);
      message.error('Failed to update completion statuses');
    } finally {
      setLoading(false);
    }
  };

  // Fetch initial statuses on mount
  useEffect(() => {
    const fetchStatuses = async () => {
      setLoading(true);
      try {
        const userId = localStorage.getItem('userid');
        console.log('[CompletionModal] Fetching initial statuses for requirement:', requirement.unid);

        // Initialize every client as 'No' by default
        const initialStatusMap = {};
        (clients || []).forEach((client) => {
          initialStatusMap[client.unid] = 'No';
        });

        // Helper to fetch with error handling
        const fetchWithErrorHandling = async (url) => {
          const response = await fetch(url);
          if (!response.ok) {
            const text = await response.text();
            console.error('[CompletionModal] API Error Response:', {
              status: response.status,
              statusText: response.statusText,
              body: text,
            });
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        };

        // API calls for all three status types
        const [checked, notRequired, pending] = await Promise.all([
          fetchWithErrorHandling(
            `https://plansync-backend-234288438538.us-central1.run.app/api/requirements-checklist/${year}/${userId}/clients-checked/${requirement.unid}`
          ),
          fetchWithErrorHandling(
            `https://plansync-backend-234288438538.us-central1.run.app/api/requirements-checklist/${year}/${userId}/not-required/${requirement.unid}`
          ),
          fetchWithErrorHandling(
            `https://plansync-backend-234288438538.us-central1.run.app/api/requirements-checklist/${year}/${userId}/clients-pending/${requirement.unid}`
          )
        ]);

        // Store API responses in state
        setCheckedData(checked);
        setNotRequiredData(notRequired);
        setPendingData(pending);

        console.log('[CompletionModal] API Response Data:', {
          checked,
          notRequired,
          pending
        });

        // Mark "Yes" clients
        if (checked?.clients_checked) {
          checked.clients_checked.forEach((clientName) => {
            const client = (clients || []).find(c => c['Company Name'] === clientName);
            if (client) {
              initialStatusMap[client.unid] = 'Yes';
            }
          });
        }

        // Mark "Not Required" clients
        if (notRequired?.not_required) {
          notRequired.not_required.forEach((clientName) => {
            const client = (clients || []).find(c => c['Company Name'] === clientName);
            if (client) {
              initialStatusMap[client.unid] = 'Not Required';
            }
          });
        }

        // Mark "No" clients (pending)
        if (pending?.pending_clients) {
          pending.pending_clients.forEach((clientName) => {
            const client = (clients || []).find(c => c['Company Name'] === clientName);
            if (client) {
              initialStatusMap[client.unid] = 'No';
            }
          });
        }

        console.log('[CompletionModal] Final initial status map:', initialStatusMap);
        setLocalStatuses(initialStatusMap);
      } catch (error) {
        console.error('[CompletionModal] Error loading statuses:', error);
        message.error(`Failed to load completion statuses: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (requirement?.unid && year && clients?.length > 0) {
      fetchStatuses();
    } else {
      console.log('[CompletionModal] Missing data, skipping fetch:', {
        requirementId: requirement?.unid,
        year,
        clientCount: clients?.length,
      });
    }
  }, [requirement?.unid, year, clients]);

  // We reuse the same status options from MasterSpreadsheet
  const statusOptions = [
    {
      value: 'Complete',
      label: 'Complete',
      color: '#16a34a',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      textColor: 'text-green-700',
    },
    {
      value: 'Not Complete',
      label: 'Not Complete',
      color: '#ea580c',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      textColor: 'text-orange-700',
    },
    {
      value: 'Not Required',
      label: 'Not Required',
      color: '#6b7280',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200',
      textColor: 'text-gray-700',
    },
  ];

  // Handle single client toggle
  const handleStatusChange = async (clientId, selectedOption) => {
    console.log('[CompletionModal] Toggling status:', {
      clientId,
      newStatus: selectedOption.value,
    });

    const newStatuses = {
      ...localStatuses,
      [clientId]: selectedOption.value,
    };
    setLocalStatuses(newStatuses);

    // Save only the changed client to server
    await saveStatusesToServer(
      Object.fromEntries(Object.entries(newStatuses).map(([k, v]) => [k, v.toLowerCase()])),
      Object.fromEntries(Object.entries(localStatuses).map(([k, v]) => [k, v.toLowerCase()])),
    );
  };

  // Optional: bulk action if desired
  const handleToggleAll = async (selectedOption) => {
    console.log('[CompletionModal] Bulk action to:', selectedOption.value);
    const newStatuses = {};
    (clients || []).forEach((client) => {
      newStatuses[client.unid] = selectedOption.value;
    });
    setLocalStatuses(newStatuses);

    await saveStatusesToServer(
      Object.fromEntries(Object.entries(newStatuses).map(([k, v]) => [k, v.toLowerCase()])),
      Object.fromEntries(Object.entries(localStatuses).map(([k, v]) => [k, v.toLowerCase()])),
    );
  };

  // Filtered list of clients by name and optional status filter
  const filteredClients = (clients || []).filter((client) => {
    const matchesSearch = client['Company Name']
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesStatus =
      !statusFilter || localStatuses[client.unid] === statusFilter.value;
    return matchesSearch && matchesStatus;
  });

  // Calculate counts based on filtered clients and their statuses
  const getCounts = () => {
    const counts = {
      complete: 0,
      notComplete: 0,
      notRequired: 0
    };

    filteredClients.forEach(client => {
      const status = localStatuses[client.unid];
      if (status === 'Yes') {
        counts.complete++;
      } else if (status === 'No') {
        counts.notComplete++;
      } else if (status === 'Not Required') {
        counts.notRequired++;
      }
    });

    return counts;
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/40 backdrop-blur-md z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        className="bg-white/90 backdrop-blur-xl rounded-3xl w-full max-w-[850px] max-h-[90vh] 
          flex flex-col shadow-[0_20px_70px_-10px_rgba(0,0,0,0.2)] overflow-hidden
          border border-white/50"
      >
        {/* Header */}
        <div
          className="px-8 py-6 bg-gradient-to-r from-slate-500/10 via-slate-600/10 to-slate-700/10
            border-b border-white/30 backdrop-blur-md"
        >
          <div className="flex justify-between items-center">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <div className="h-8 w-1 rounded-full bg-gradient-to-b from-sky-400 to-sky-500" />
                <h2
                  className="text-xl font-semibold bg-gradient-to-r from-gray-800 to-gray-600 
                    bg-clip-text text-transparent"
                >
                  Update Requirement Status
                </h2>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">
                {requirement?.requirement || requirement?.Requirement}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2.5 hover:bg-white/50 rounded-xl transition-all duration-300
                hover:shadow-lg hover:scale-105 group"
            >
              <XCircle
                size={24}
                className="text-gray-400 group-hover:text-gray-600 transition-colors duration-300"
              />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8 space-y-6 relative">
          {loading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 z-10 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          )}

          {/* Search and Filters */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 group">
              <input
                type="text"
                placeholder="Search clients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3.5 bg-white/50 border border-gray-200 rounded-2xl text-sm
                  transition-all duration-300 placeholder:text-gray-400
                  focus:border-blue-400 focus:ring-4 focus:ring-blue-100 focus:bg-white
                  group-hover:border-gray-300 group-hover:shadow-md"
              />
              <SearchIcon
                size={18}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400
                  group-hover:text-gray-600 transition-colors duration-300"
              />
            </div>

            {/* <div className="relative w-48">
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: null, label: 'All Statuses' },
                  ...statusOptions,
                ]}
                placeholder="Filter by status..."
                className="w-48"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: '1rem',
                    borderColor: '#e5e7eb',
                    minHeight: '52px',
                    backgroundColor: 'rgba(255,255,255,0.5)',
                    boxShadow: 'none',
                    '&:hover': {
                      borderColor: '#60a5fa',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                    },
                  }),
                  option: (base, { data, isFocused, isSelected }) => ({
                    ...base,
                    backgroundColor: isSelected
                      ? data.color || '#3b82f6'
                      : isFocused
                      ? data.color
                        ? `${data.color}15`
                        : '#f3f4f6'
                      : 'transparent',
                    color: isSelected ? 'white' : data.color || '#374151',
                    fontWeight: '500',
                  }),
                }}
              />
              {statusFilter && (
                <button
                  onClick={() => setStatusFilter(null)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full"
                >
                  <X size={14} className="text-gray-500" />
                </button>
              )}
            </div> */}
          </div>

          {/* Progress Stats */}
          <div className="mb-6">
            {/* Status Count Cards */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              {(() => {
                const counts = getCounts();
                return [
                  { 
                    label: 'Complete', 
                    count: counts.complete,
                    bgColor: 'bg-green-50',
                    borderColor: 'border-green-200',
                    textColor: 'text-green-700'
                  },
                  { 
                    label: 'Not Complete', 
                    count: counts.notComplete,
                    bgColor: 'bg-red-50',
                    borderColor: 'border-red-200',
                    textColor: 'text-red-700'
                  },
                  { 
                    label: 'Not Required', 
                    count: counts.notRequired,
                    bgColor: 'bg-gray-50',
                    borderColor: 'border-gray-200',
                    textColor: 'text-gray-700'
                  }
                ].map(status => (
                  <div
                    key={status.label}
                    className={`${status.bgColor} ${status.borderColor} border rounded-2xl p-4 shadow-sm`}
                  >
                    <div className={`text-2xl font-bold ${status.textColor}`}>
                      {status.count}
                    </div>
                    <div className="text-sm text-gray-600">
                      {status.label}
                    </div>
                  </div>
                ));
              })()}
            </div>
            
            {/* Overall Progress Bar */}
            <div className="bg-white/50 border border-gray-200 rounded-2xl p-4 shadow-sm">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-600">Overall Progress</span>
                {(() => {
                  const counts = getCounts();
                  const total = counts.complete + counts.notComplete;
                  const percentage = total > 0 ? Math.round((counts.complete / total) * 100) : 0;
                  return (
                    <span className="text-sm font-medium text-gray-600">
                      {percentage}% Complete
                    </span>
                  );
                })()}
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-500 transition-all duration-500 ease-out"
                  style={{
                    width: (() => {
                      const counts = getCounts();
                      const total = counts.complete + counts.notComplete;
                      return total > 0 ? `${(counts.complete / total) * 100}%` : '0%';
                    })()
                  }}
                />
              </div>
            </div>
          </div>

          {/* Client List */}
          {filteredClients.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No clients match your current filters
            </div>
          ) : (
            <div className="space-y-3">
              {filteredClients.map((client) => (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  key={client.unid}
                  className={`flex items-center justify-between p-5
                    rounded-2xl transition-all duration-300 group
                    hover:shadow-lg border hover:scale-[1.01]
                    ${
                      localStatuses[client.unid] === 'Yes'
                        ? 'bg-green-50/50 hover:bg-green-50 border-green-200/50'
                        : localStatuses[client.unid] === 'No'
                        ? 'bg-orange-50/50 hover:bg-orange-50 border-orange-200/50'
                        : 'bg-gray-50/50 hover:bg-gray-50 border-gray-200/50'
                    }`}
                >
                  <div className="flex items-center gap-4">
                    {/* Colored status dot */}
                    <div
                      className={`w-3 h-3 rounded-full ring-2 ring-white shadow-sm transition-all duration-300 ${
                        localStatuses[client.unid] === 'Yes'
                          ? 'bg-green-500 ring-green-200'
                          : localStatuses[client.unid] === 'No'
                          ? 'bg-orange-500 ring-orange-200'
                          : 'bg-gray-500 ring-gray-200'
                      }`}
                    />
                    <span className="text-[0.95rem] font-medium text-gray-700">
                      {client['Company Name']}
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      // Cycle through statuses in a quick toggle
                      const currentStatus = localStatuses[client.unid];
                      let nextStatus;
                      switch (currentStatus) {
                        case 'No':
                          nextStatus = 'Yes';
                          break;
                        case 'Yes':
                          nextStatus = 'Not Required';
                          break;
                        case 'Not Required':
                        default:
                          nextStatus = 'No';
                          break;
                      }
                      handleStatusChange(client.unid, {
                        value: nextStatus,
                        label: nextStatus,
                      });
                    }}
                    className={`px-4 py-2 rounded-xl transition-all duration-300 font-medium text-sm
                      ${
                        localStatuses[client.unid] === 'Yes'
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : localStatuses[client.unid] === 'No'
                          ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }
                      hover:shadow-md hover:scale-[1.02]`}
                  >
                    {localStatuses[client.unid] === 'Yes'
                      ? 'Complete'
                      : localStatuses[client.unid] === 'No'
                      ? 'Not Complete'
                      : 'Not Required'}
                  </button>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="px-8 py-6 bg-gradient-to-r from-gray-50 to-white border-t border-gray-100
            backdrop-blur-md flex justify-end"
        >
          <button
            onClick={onClose}
            className="px-6 py-3 text-gray-600 font-medium hover:bg-gray-100 
              rounded-xl transition-all duration-300 hover:shadow-md hover:scale-[1.02]"
          >
            Close
          </button>
        </div>
      </motion.div>
    </div>,
    document.body
  );
};

export default CompletionModal;